'use client'

import React, { memo } from 'react'

// Types compatibles avec page.tsx
interface PanelElementProps {
  element: any // WorkspaceElement
  panel: any // LEDPanel
  isSelected: boolean
  isDragging: boolean
  isCurrentForCabling: boolean
  isGridNavigationSelected?: boolean  // Nouveau: pour la navigation au clavier
  currentStep: string
  exportViewMode?: 'data' | 'power'
  selectedPort: number | null
  selectedProcessorForConnection: string | null
  panelConnections: Array<{
    processorId: string
    panelId: string
    port: number
    order: number
  }>
  powerConnections: Array<{
    cabinetId: string
    panelId: string
    outlet: number
    order: number
  }>
  backupPorts: Record<string, Record<number, number>>
  cableRoutes: Array<{
    id: string
    from: string
    to: string
    points: Array<{ x: number; y: number }>
    color?: string
    type?: string
  }>
  workspaceElements: any[]
  getPortColor: (port: number) => string
  getOutletColor: (outlet: number) => string
  getBackupForPort: (port: number, processorId: string) => number | null
  onClick: (element: any) => void
  onContextMenu?: (e: React.MouseEvent, element: any) => void
  showResizeHandle: boolean
  onResizeStart?: (e: React.MouseEvent, element: any) => void
}

const PanelElement = memo(({
  element,
  panel,
  isSelected,
  isDragging,
  isCurrentForCabling,
  isGridNavigationSelected = false,
  currentStep,
  exportViewMode = 'data',
  selectedPort,
  selectedProcessorForConnection,
  panelConnections,
  powerConnections,
  backupPorts,
  cableRoutes,
  workspaceElements,
  getPortColor,
  getOutletColor,
  getBackupForPort,
  onClick,
  onContextMenu,
  showResizeHandle,
  onResizeStart
}: PanelElementProps) => {
  
  // Calculer la position optimale du numéro pour éviter les câbles
  const calculateOptimalNumberPosition = (connection: any) => {
    if (!connection) {
      return {
        x: element.position.x + element.size.width / 2,
        y: element.position.y + element.size.height / 2,
        textAnchor: "middle" as const,
        fill: "#FFFFFF",
        isCornerPosition: false
      }
    }
    
    // Analyser les connexions pour déterminer la position du panneau
    const allConnections = panelConnections.filter(conn => 
      conn.processorId === connection.processorId && 
      conn.port === connection.port
    ).sort((a, b) => a.order - b.order)
    
    const cables = cableRoutes.filter(cable => 
      cable.from === element.id || cable.to === element.id
    )
    
    const textColor = "#FFFFFF"
    
    if (cables.length === 0) {
      return {
        x: element.position.x + element.size.width / 2,
        y: element.position.y + element.size.height / 2,
        textAnchor: "middle" as const,
        fill: textColor,
        isCornerPosition: false
      }
    }
    
    // Analyser les directions des câbles
    const directions = {
      top: false,
      bottom: false,
      left: false,
      right: false
    }
    
    cables.forEach(cable => {
      const isIncoming = cable.to === element.id
      const otherPoint = isIncoming ? cable.points[0] : cable.points[1]
      if (!otherPoint || typeof otherPoint.x !== 'number' || typeof otherPoint.y !== 'number') return
      const elementCenter = {
        x: element.position.x + element.size.width / 2,
        y: element.position.y + element.size.height / 2
      }
      
      const dx = otherPoint.x - elementCenter.x
      const dy = otherPoint.y - elementCenter.y
      
      if (Math.abs(dx) > Math.abs(dy)) {
        if (dx > 0) directions.right = true
        else directions.left = true
      } else {
        if (dy > 0) directions.bottom = true
        else directions.top = true
      }
    })
    
    // Choisir le coin le moins encombré
    const corners = [
      { name: 'topLeft', blocked: directions.top || directions.left, x: element.position.x + 8, y: element.position.y + 12, anchor: "start" as const },
      { name: 'topRight', blocked: directions.top || directions.right, x: element.position.x + element.size.width - 8, y: element.position.y + 12, anchor: "end" as const },
      { name: 'bottomLeft', blocked: directions.bottom || directions.left, x: element.position.x + 8, y: element.position.y + element.size.height - 8, anchor: "start" as const },
      { name: 'bottomRight', blocked: directions.bottom || directions.right, x: element.position.x + element.size.width - 8, y: element.position.y + element.size.height - 8, anchor: "end" as const }
    ]
    
    const bestCorner = corners.find(corner => !corner.blocked) || corners[0]
    
    return {
      x: bestCorner.x,
      y: bestCorner.y,
      textAnchor: bestCorner.anchor,
      fill: textColor,
      isCornerPosition: true
    }
  }

  // Récupérer l'opacité du panneau (1 par défaut)
  const panelOpacity = panel.opacity !== undefined ? panel.opacity : 1
  
  // Vérifier si le panneau contient une image de mire
  const hasMireImage = panel.mireImage && panel.mireImagePortion
  
  // Vérifier si l'élément fait partie d'un groupe
  const isInGroup = !!element.groupId

  return (
    <g>
      {/* Panel principal - Style COEX VMP */}
      {!hasMireImage && (
        <rect
          x={element.position.x}
          y={element.position.y}
          width={element.size.width}
          height={element.size.height}
          fill="#B8B8B8"
          fillOpacity={panelOpacity}
          stroke={isInGroup ? "none" : "#2D2D2D"}
          strokeWidth={isInGroup ? "0" : "2"}
          className={`${(currentStep === 'cabling' || (currentStep === 'export' && exportViewMode === 'power')) ? 'cursor-pointer' : 'cursor-move'} ${isDragging ? 'opacity-80' : ''}`}
          onClick={() => onClick(element)}
          onContextMenu={(e) => {
            if (onContextMenu) {
              e.preventDefault()
              onContextMenu(e, element)
            }
          }}
        />
      )}
      
      {/* Panneau avec image de mire intégrée */}
      {hasMireImage && (
        <>
          <defs>
            <clipPath id={`clip-${element.id}`}>
              <rect
                x={element.position.x}
                y={element.position.y}
                width={element.size.width}
                height={element.size.height}
              />
            </clipPath>
          </defs>
          <image
            x={element.position.x - (panel.mireImagePortion.x / 100) * element.size.width * (100 / panel.mireImagePortion.width)}
            y={element.position.y - (panel.mireImagePortion.y / 100) * element.size.height * (100 / panel.mireImagePortion.height)}
            width={element.size.width * (100 / panel.mireImagePortion.width)}
            height={element.size.height * (100 / panel.mireImagePortion.height)}
            href={panel.mireImage}
            preserveAspectRatio="none"
            opacity={panelOpacity}
            clipPath={`url(#clip-${element.id})`}
            className={isDragging ? 'opacity-80' : ''}
            style={{ pointerEvents: 'none' }}
          />
          {/* Zone cliquable invisible pour les interactions (toujours présente) */}
          <rect
            x={element.position.x}
            y={element.position.y}
            width={element.size.width}
            height={element.size.height}
            onContextMenu={(e) => {
              if (onContextMenu) {
                e.preventDefault()
                onContextMenu(e, element)
              }
            }}
            fill="transparent"
            stroke="none"
            className={`${(currentStep === 'cabling' || (currentStep === 'export' && exportViewMode === 'power')) ? 'cursor-pointer' : 'cursor-move'}`}
            onClick={() => onClick(element)}
            style={{ pointerEvents: 'auto' }}
          />
        </>
      )}
      
      {/* Indicateur de panneau actif pour câblage Power */}
      {isCurrentForCabling && currentStep === 'cabling' && (
        <>
          <rect
            x={element.position.x - 4}
            y={element.position.y - 4}
            width={element.size.width + 8}
            height={element.size.height + 8}
            fill="none"
            stroke="#DC2626"
            strokeWidth="3"
            strokeDasharray="8,4"
            className="animate-pulse"
          />
          <text
            x={element.position.x + element.size.width / 2}
            y={element.position.y - 10}
            fontSize="12"
            fontWeight="bold"
            fill="#DC2626"
            textAnchor="middle"
            className="animate-pulse"
          >
            ⚡ Actif
          </text>
        </>
      )}
      
      {/* Overlay de sélection - Uniquement si pas dans un groupe (le groupe affichera son propre contour) */}
      {isSelected && !isInGroup && (
        <rect
          x={element.position.x}
          y={element.position.y}
          width={element.size.width}
          height={element.size.height}
          fill="hsl(var(--primary) / 0.2)"
          stroke="hsl(var(--primary))"
          strokeWidth="3"
          strokeDasharray="5,3"
        />
      )}
      
      {/* Indicateur de navigation au clavier */}
      {isGridNavigationSelected && (
        <>
          <rect
            x={element.position.x - 6}
            y={element.position.y - 6}
            width={element.size.width + 12}
            height={element.size.height + 12}
            fill="rgba(16, 185, 129, 0.2)"
            stroke="#10B981"
            strokeWidth="5"
            rx="4"
            pointerEvents="none"
          >
            <animate attributeName="stroke-opacity" values="1;0.4;1" dur="1s" repeatCount="indefinite" />
          </rect>
          <text
            x={element.position.x + element.size.width / 2}
            y={element.position.y - 12}
            fontSize="14"
            fontWeight="bold"
            fill="#10B981"
            textAnchor="middle"
            pointerEvents="none"
          >
            ⬅️ ➡️ ⬆️ ⬇️
          </text>
        </>
      )}
      
      {/* Numérotation pour l'étape Power (cabling ou export en mode power) */}
      {(currentStep === 'cabling' || (currentStep === 'export' && exportViewMode === 'power')) && (() => {
        const powerConns = powerConnections.filter(c => c.panelId === element.id)
        
        if (powerConns.length > 0) {
          return (
            <g>
              {powerConns.map((powerConn, index) => {
                const outletConnections = powerConnections.filter(conn => 
                  conn.cabinetId === powerConn.cabinetId && 
                  conn.outlet === powerConn.outlet
                ).sort((a, b) => a.order - b.order)
                
                const isFirstInChain = outletConnections[0]?.panelId === element.id
                const outletColor = getOutletColor(powerConn.outlet)
                
                // Créer un pseudo-connection pour calculateOptimalNumberPosition
                const pseudoConnection = {
                  panelId: powerConn.panelId,
                  processorId: powerConn.cabinetId,
                  port: powerConn.outlet,
                  order: powerConn.order
                }
                const numberPos = calculateOptimalNumberPosition(pseudoConnection)
                
                return (
                  <g key={`power-${powerConn.outlet}-${index}`}>
                    {/* Badge coloré pour le premier panneau de la chaîne */}
                    {isFirstInChain && (
                      <>
                        <rect
                          x={element.position.x}
                          y={element.position.y}
                          width={element.size.width}
                          height={element.size.height}
                          fill={outletColor}
                          fillOpacity="0.8"
                          rx="4"
                        />
                        <text
                          x={element.position.x + element.size.width / 2}
                          y={element.position.y + element.size.height / 2}
                          fontSize={Math.max(8, element.size.width * 0.3)}
                          fontWeight="bold"
                          fill="white"
                          textAnchor="middle"
                          dominantBaseline="central"
                        >
                          PC{powerConn.outlet}
                        </text>
                      </>
                    )}
                    
                    {/* Numéro principal */}
                    <text
                      x={numberPos.x}
                      y={numberPos.y}
                      fontSize={Math.max(8, element.size.width * (isFirstInChain ? 0.23 : 0.27))}
                      fontWeight="bold"
                      fill={numberPos.fill}
                      textAnchor={numberPos.textAnchor}
                      dominantBaseline="central"
                    >
                      {powerConn.order}
                    </text>
                  </g>
                )
              })}
            </g>
          )
        }
        
        // Si aucune connexion power, ne pas afficher de numéro
        return null
      })()}
      
      {/* Numérotation pour l'étape Data (ou export en mode data) */}
      {(currentStep === 'data' || (currentStep === 'export' && exportViewMode === 'data')) && (() => {
        // Si aucun port n'est sélectionné, afficher toutes les connexions
        if (selectedPort === null) {
          const allConnections = panelConnections.filter(c => c.panelId === element.id)
          if (allConnections.length > 0) {
            // Regrouper les connexions par port
            const connectionsByPort = allConnections.reduce((acc, conn) => {
              if (!acc[conn.port]) acc[conn.port] = []
              acc[conn.port].push(conn)
              return acc
            }, {} as Record<number, any[]>)
            
            return (
              <g>
                {Object.entries(connectionsByPort).map(([portStr, connections]) => {
                  const port = parseInt(portStr)
                  const connection = connections[0]
                  
                  const portConnections = panelConnections.filter(conn => 
                    conn.processorId === connection.processorId && 
                    conn.port === port
                  ).sort((a, b) => a.order - b.order)
                  
                  const isFirstPanel = portConnections[0]?.panelId === element.id
                  const isLastPanel = portConnections[portConnections.length - 1]?.panelId === element.id
                  const hasBackup = getBackupForPort(port, connection.processorId)
                  const numberPos = calculateOptimalNumberPosition(connection)
                  
                  return (
                    <g key={`port-${port}`}>
                      {/* Badge PX pour le premier panneau */}
                      {isFirstPanel && (
                        <>
                          <rect
                            x={element.position.x}
                            y={element.position.y}
                            width={element.size.width}
                            height={element.size.height}
                            fill={getPortColor(port)}
                            fillOpacity="0.8"
                            rx="4"
                          />
                          <text
                            x={element.position.x + element.size.width / 2}
                            y={element.position.y + element.size.height / 2}
                            fontSize={Math.max(8, element.size.width * 0.3)}
                            fontWeight="bold"
                            fill="white"
                            textAnchor="middle"
                            dominantBaseline="central"
                          >
                            P{port}
                          </text>
                        </>
                      )}
                      
                      {/* Badge RY pour le dernier panneau avec backup */}
                      {isLastPanel && hasBackup && (
                        <>
                          <rect
                            x={element.position.x}
                            y={element.position.y}
                            width={element.size.width}
                            height={element.size.height}
                            fill={getPortColor(hasBackup)}
                            fillOpacity="0.8"
                            rx="4"
                          />
                          <text
                            x={element.position.x + element.size.width / 2}
                            y={element.position.y + element.size.height / 2}
                            fontSize={Math.max(8, element.size.width * 0.3)}
                            fontWeight="bold"
                            fill="white"
                            textAnchor="middle"
                            dominantBaseline="central"
                          >
                            R{hasBackup}
                          </text>
                        </>
                      )}
                      
                      {/* Numéro du port */}
                      <text
                        x={numberPos.x}
                        y={numberPos.y}
                        fontSize={Math.max(8, element.size.width * 0.27)}
                        fontWeight="bold"
                        fill={numberPos.fill}
                        textAnchor={numberPos.textAnchor}
                        dominantBaseline="central"
                      >
                        {connection.order}
                      </text>
                    </g>
                  )
                })}
              </g>
            )
          }
          
          // Si aucune connexion, ne pas afficher de numéro
          return null
        }
        
        // Si un port spécifique est sélectionné
        const connection = panelConnections.find(conn => 
          conn.panelId === element.id && 
          conn.processorId === selectedProcessorForConnection && 
          conn.port === selectedPort
        )
        
        if (connection) {
          const portConnections = panelConnections.filter(conn => 
            conn.processorId === connection.processorId && 
            conn.port === selectedPort
          ).sort((a, b) => a.order - b.order)
          
          const isFirstPanel = portConnections[0]?.panelId === element.id
          const isLastPanel = portConnections[portConnections.length - 1]?.panelId === element.id
          const hasBackup = getBackupForPort(selectedPort, connection.processorId)
          const numberPos = calculateOptimalNumberPosition(connection)
          
          return (
            <g>
              {/* Badge PX */}
              {isFirstPanel && (
                <>
                  <rect
                    x={element.position.x}
                    y={element.position.y}
                    width={element.size.width}
                    height={element.size.height}
                    fill={getPortColor(selectedPort)}
                    fillOpacity="0.8"
                    rx="4"
                  />
                  <text
                    x={element.position.x + element.size.width / 2}
                    y={element.position.y + element.size.height / 2}
                    fontSize={Math.max(8, element.size.width * 0.3)}
                    fontWeight="bold"
                    fill="white"
                    textAnchor="middle"
                    dominantBaseline="central"
                  >
                    P{selectedPort}
                  </text>
                </>
              )}
              
              {/* Badge RY */}
              {isLastPanel && hasBackup && (
                <>
                  <rect
                    x={element.position.x}
                    y={element.position.y}
                    width={element.size.width}
                    height={element.size.height}
                    fill={getPortColor(hasBackup)}
                    fillOpacity="0.8"
                    rx="4"
                  />
                  <text
                    x={element.position.x + element.size.width / 2}
                    y={element.position.y + element.size.height / 2}
                    fontSize={Math.max(8, element.size.width * 0.3)}
                    fontWeight="bold"
                    fill="white"
                    textAnchor="middle"
                    dominantBaseline="central"
                  >
                    R{hasBackup}
                  </text>
                </>
              )}
              
              {/* Numéro */}
              <text
                x={numberPos.x}
                y={numberPos.y}
                fontSize={Math.max(8, element.size.width * 0.27)}
                fontWeight="bold"
                fill={numberPos.fill}
                textAnchor={numberPos.textAnchor}
                dominantBaseline="central"
              >
                {connection.order}
              </text>
            </g>
          )
        }
        
        // Si pas connecté au port sélectionné, ne pas afficher de numéro
        return null
      })()}
      
      {/* Handle de redimensionnement */}
      {showResizeHandle && onResizeStart && (
        <g
          data-resize-handle="true"
          className="cursor-nwse-resize"
          style={{ userSelect: 'none', pointerEvents: 'auto' } as any}
          onMouseDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onResizeStart(e, element)
          }}
        >
          <rect
            data-resize-handle="true"
            x={element.position.x + element.size.width - 7}
            y={element.position.y + element.size.height - 7}
            width="12"
            height="12"
            fill="hsl(var(--primary))"
            stroke="hsl(var(--background))"
            strokeWidth="1.5"
            rx="2"
          />
          <line
            x1={element.position.x + element.size.width - 3}
            y1={element.position.y + element.size.height + 2}
            x2={element.position.x + element.size.width + 2}
            y2={element.position.y + element.size.height - 3}
            stroke="hsl(var(--background))"
            strokeWidth="1"
          />
          <line
            x1={element.position.x + element.size.width - 3}
            y1={element.position.y + element.size.height - 1}
            x2={element.position.x + element.size.width - 1}
            y2={element.position.y + element.size.height - 3}
            stroke="hsl(var(--background))"
            strokeWidth="1"
          />
        </g>
      )}
    </g>
  )
}, (prevProps, nextProps) => {
  // Comparaison personnalisée pour éviter les re-renders inutiles
  
  if (
    prevProps.element.id !== nextProps.element.id ||
    prevProps.element.position.x !== nextProps.element.position.x ||
    prevProps.element.position.y !== nextProps.element.position.y ||
    prevProps.element.size.width !== nextProps.element.size.width ||
    prevProps.element.size.height !== nextProps.element.size.height ||
    prevProps.isSelected !== nextProps.isSelected ||
    prevProps.isDragging !== nextProps.isDragging ||
    prevProps.isCurrentForCabling !== nextProps.isCurrentForCabling ||
    prevProps.currentStep !== nextProps.currentStep ||
    prevProps.selectedPort !== nextProps.selectedPort ||
    prevProps.selectedProcessorForConnection !== nextProps.selectedProcessorForConnection ||
    prevProps.showResizeHandle !== nextProps.showResizeHandle
  ) {
    return false
  }
  
  // Comparer les connexions pour ce panneau uniquement
  const prevPanelConns = prevProps.panelConnections.filter(c => c.panelId === prevProps.element.id)
  const nextPanelConns = nextProps.panelConnections.filter(c => c.panelId === nextProps.element.id)
  
  if (prevPanelConns.length !== nextPanelConns.length) {
    return false
  }
  
  const prevPowerConns = prevProps.powerConnections.filter(c => c.panelId === prevProps.element.id)
  const nextPowerConns = nextProps.powerConnections.filter(c => c.panelId === nextProps.element.id)
  
  if (prevPowerConns.length !== nextPowerConns.length) {
    return false
  }
  
  // Comparer les câbles connectés à ce panneau
  const prevCables = prevProps.cableRoutes.filter(c => c.from === prevProps.element.id || c.to === prevProps.element.id)
  const nextCables = nextProps.cableRoutes.filter(c => c.from === nextProps.element.id || c.to === nextProps.element.id)
  
  if (prevCables.length !== nextCables.length) {
    return false
  }
  
  return true
})

PanelElement.displayName = 'PanelElement'

export default PanelElement
